import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  GestureResponderEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MB_Modal } from '@/components/general/modal';
import { MB_VideoPlayer, MB_VIDEO_PLAYER_RESIZE_MODE } from '@/components/general/video-player';
import { MB_Image, mbGetMediumImage } from '@/components/general/image';
import { MB_customWindowForWeb } from '@/utils/general/custom-window-for-web';
import { mbApplyTransparency, mbTextStyles } from '@/utils/general/style-utils';
import { isMobileApp } from '@/utils/general/device-info';
import { PoolVideo } from '../../../typesAndInterfaces/typesAndInterfaces';
import { COLORS } from '../../../constants/colors';
import { textStyles } from '../../../constants/textStyles';

/**
 * Full-screen playlist viewer for a Lessons (Extras) collection.
 *
 * - Opens at `initialIndex`, swipe left/right (mobile) or arrows / keyboard (web)
 *   to move between videos. No wrapping: the ends show a short cue instead.
 * - The active video loops until the user moves on or closes.
 * - Only the active page plays. The page on each side is mounted (paused) so the
 *   player has already prepared the stream when the user swipes.
 * - Native controls are off on every platform (see PR notes: Android's expo-av
 *   MediaController consumes every touch while visible, iOS's
 *   AVPlayerViewController would sit under any gesture layer). Controls are the
 *   minimal custom set: play/pause, progress, close.
 *
 * Callers must pass only playable videos (no `isProcessing` entries).
 */

interface LessonsPlaylistViewerProps {
  videos: PoolVideo[];
  initialIndex: number;
  isVisible: boolean;
  /** Called after the close animation finishes. */
  onClosed: () => void;
}

const SWIPE_THRESHOLD = 30;
const END_CUE_DURATION = 1300;

const isWeb = Platform.OS === 'web';

// The project compiles without the DOM lib, so the bits of the browser we touch are typed here.
interface WebKeyboardEvent {
  key: string;
  preventDefault: () => void;
}
interface WebFocusableElement {
  tagName?: string;
  isContentEditable?: boolean;
}
const webWindow = MB_customWindowForWeb as unknown as {
  addEventListener?: (type: string, listener: (event: WebKeyboardEvent) => void) => void;
  removeEventListener?: (type: string, listener: (event: WebKeyboardEvent) => void) => void;
  document?: { activeElement?: WebFocusableElement | null };
};

/** True when a text field owns keyboard focus, so the viewer must not react to keys. */
const isTextInputFocused = (): boolean => {
  if (!isWeb) {
    return false;
  }
  const element = webWindow.document?.activeElement;
  if (!element) {
    return false;
  }
  const tag = element.tagName?.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable === true;
};

const LessonsPlaylistViewer = ({ videos, initialIndex, isVisible, onClosed }: LessonsPlaylistViewerProps) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsClosing(false);
    }
  }, [isVisible]);

  const requestClose = useCallback(() => setIsClosing(true), []);

  return (
    <MB_Modal
      isVisible={isVisible && !isClosing}
      onDismiss={onClosed}
      simplified
      hideCloseButton
      backdropOpacity={1}
      childrenWrapperStyle={styles.modalChildrenWrapper}
      closeOnBackdropPress={false}
      animationInTiming={200}
      animationOutTiming={200}
    >
      <PlaylistContent videos={videos} initialIndex={initialIndex} onRequestClose={requestClose} />
    </MB_Modal>
  );
};

interface PlaylistContentProps {
  videos: PoolVideo[];
  initialIndex: number;
  onRequestClose: () => void;
}

const PlaylistContent = ({ videos, initialIndex, onRequestClose }: PlaylistContentProps) => {
  const { width, height } = useWindowDimensions();
  const listRef = useRef<ScrollView>(null);
  // The modal's own SafeAreaView reports no insets inside the iOS modal window,
  // so pad the bars from the root provider's insets there. Android's status bar
  // already sits above the modal content.
  const insets = useSafeAreaInsets();
  const topInset = Platform.OS === 'ios' ? insets.top : 0;
  const bottomInset = Platform.OS === 'ios' ? insets.bottom : 0;

  const clampedInitialIndex = Math.min(Math.max(initialIndex, 0), Math.max(videos.length - 1, 0));
  const [activeIndex, setActiveIndex] = useState(clampedInitialIndex);
  const activeIndexRef = useRef(clampedInitialIndex);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState({ positionMillis: 0, durationMillis: 0 });
  const [endCue, setEndCue] = useState<'first' | 'last' | undefined>();

  const lastIndex = videos.length - 1;
  const hasMultiple = videos.length > 1;

  // Stable object: Android re-applies `contentOffset` whenever the prop identity
  // changes, which would yank the list back to the opening page on every render.
  const initialContentOffset = useMemo(() => ({ x: clampedInitialIndex * width, y: 0 }), [clampedInitialIndex, width]);

  const goToIndex = useCallback(
    (nextIndex: number, animated = true) => {
      if (nextIndex < 0) {
        setEndCue('first');
        return;
      }
      if (nextIndex > lastIndex) {
        setEndCue('last');
        return;
      }
      activeIndexRef.current = nextIndex;
      setActiveIndex(nextIndex);
      setIsPaused(false);
      setProgress({ positionMillis: 0, durationMillis: 0 });
      listRef.current?.scrollTo({ x: nextIndex * width, animated });
    },
    [lastIndex, width]
  );

  // Rotation / window resize: keep the active page aligned to the new width.
  useEffect(() => {
    if (!hasMultiple) {
      return;
    }
    const timeout = setTimeout(() => listRef.current?.scrollTo({ x: activeIndexRef.current * width, animated: false }), 0);
    return () => clearTimeout(timeout);
  }, [width, hasMultiple]);

  // Web keyboard: only while the viewer is mounted (it unmounts on close), and
  // never when a text field has focus.
  useEffect(() => {
    if (!isWeb || !webWindow.addEventListener || !webWindow.removeEventListener) {
      return;
    }
    const onKeyDown = (event: WebKeyboardEvent) => {
      if (isTextInputFocused()) {
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onRequestClose();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToIndex(activeIndexRef.current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToIndex(activeIndexRef.current - 1);
      } else if (event.key === ' ') {
        event.preventDefault();
        setIsPaused(prev => !prev);
      }
    };
    webWindow.addEventListener('keydown', onKeyDown);
    return () => webWindow.removeEventListener?.('keydown', onKeyDown);
  }, [goToIndex, onRequestClose]);

  // End-of-list cue: auto hide.
  const endCueOpacity = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!endCue) {
      return;
    }
    endCueOpacity.setValue(0);
    Animated.sequence([
      Animated.timing(endCueOpacity, { toValue: 1, duration: 150, useNativeDriver: !isWeb }),
      Animated.delay(END_CUE_DURATION),
      Animated.timing(endCueOpacity, { toValue: 0, duration: 250, useNativeDriver: !isWeb }),
    ]).start(({ finished }) => finished && setEndCue(undefined));
  }, [endCue, endCueOpacity]);

  // Native swipes: paging moves the ScrollView; we derive the active index from
  // the offset. Android does not reliably emit onMomentumScrollEnd for the
  // post-touch paging snap, so onScroll (throttled) and onScrollEndDrag feed the
  // same sync and the page becomes active as soon as it is the majority visible.
  const syncIndexFromOffset = useCallback(
    (offsetX: number) => {
      const nextIndex = Math.round(offsetX / width);
      if (nextIndex !== activeIndexRef.current && nextIndex >= 0 && nextIndex <= lastIndex) {
        activeIndexRef.current = nextIndex;
        setActiveIndex(nextIndex);
        setIsPaused(false);
        setProgress({ positionMillis: 0, durationMillis: 0 });
      }
    },
    [lastIndex, width]
  );
  const onScrollSync = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => syncIndexFromOffset(event.nativeEvent.contentOffset.x), [syncIndexFromOffset]);

  // Raw touch tracking, used only to detect a swipe past either end (the list
  // itself cannot move there, so paging events never fire).
  const touchStartX = useRef<number | undefined>(undefined);
  const onTouchStart = useCallback((event: GestureResponderEvent) => {
    touchStartX.current = event.nativeEvent.pageX;
  }, []);
  const onTouchEnd = useCallback(
    (event: GestureResponderEvent) => {
      if (touchStartX.current === undefined) {
        return;
      }
      const deltaX = event.nativeEvent.pageX - touchStartX.current;
      touchStartX.current = undefined;
      if (deltaX < -SWIPE_THRESHOLD && activeIndexRef.current === lastIndex) {
        setEndCue('last');
      } else if (deltaX > SWIPE_THRESHOLD && activeIndexRef.current === 0) {
        setEndCue('first');
      }
    },
    [lastIndex]
  );

  const togglePause = useCallback(() => setIsPaused(prev => !prev), []);

  const onProgressUpdate = useCallback((positionMillis: number, durationMillis: number) => {
    setProgress({ positionMillis, durationMillis });
  }, []);

  // Every page is rendered; a page only hosts a player while it is within one
  // of the active index (see `isMounted`), the rest are thumbnails. Collections
  // are short, so this is cheaper and more predictable than list virtualisation
  // (which leaves the page before the opening index unrendered until a scroll).
  const pages = videos.map((video, index) => (
    <PlaylistPage
      key={video._id}
      video={video}
      width={width}
      height={height}
      isActive={index === activeIndex}
      isMounted={Math.abs(index - activeIndex) <= 1}
      isPaused={isPaused}
      onProgressUpdate={index === activeIndex ? onProgressUpdate : undefined}
      onTogglePause={togglePause}
    />
  ));

  const progressRatio = progress.durationMillis > 0 ? Math.min(progress.positionMillis / progress.durationMillis, 1) : 0;
  const activeVideo = videos[activeIndex];

  return (
    <View style={styles.container} onTouchStart={hasMultiple ? onTouchStart : undefined} onTouchEnd={hasMultiple ? onTouchEnd : undefined}>
      <ScrollView
        ref={listRef}
        horizontal
        pagingEnabled
        scrollEnabled={hasMultiple && !isWeb}
        bounces={hasMultiple}
        showsHorizontalScrollIndicator={false}
        contentOffset={initialContentOffset}
        // react-native-web ignores contentOffset, so position the page on layout there.
        onLayout={isWeb ? () => listRef.current?.scrollTo({ x: activeIndexRef.current * width, animated: false }) : undefined}
        onScroll={onScrollSync}
        scrollEventThrottle={32}
        onScrollEndDrag={onScrollSync}
        onMomentumScrollEnd={onScrollSync}
        style={styles.list}
      >
        {pages}
      </ScrollView>

      {/* Controls overlay: box-none so swipes on empty areas still reach the list. */}
      <View style={styles.overlay} pointerEvents="box-none">
        <View style={[styles.topBar, topInset > 0 && { paddingTop: topInset + 8 }]} pointerEvents="box-none">
          <Text style={styles.counterText} numberOfLines={1}>
            {hasMultiple ? `${activeIndex + 1} / ${videos.length}` : ''}
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={onRequestClose}
            style={styles.closeButton}
            hitSlop={hitSlop}
            testID="lessons-playlist-close"
          >
            <Feather name="x" size={26} color={COLORS.white} />
          </TouchableOpacity>
        </View>

        {isWeb && hasMultiple && (
          <>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel="Previous video"
              onPress={() => goToIndex(activeIndex - 1)}
              style={[styles.arrowButton, styles.arrowLeft, activeIndex === 0 && styles.arrowDisabled]}
              testID="lessons-playlist-prev"
            >
              <Feather name="chevron-left" size={36} color={COLORS.white} />
            </TouchableOpacity>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel="Next video"
              onPress={() => goToIndex(activeIndex + 1)}
              style={[styles.arrowButton, styles.arrowRight, activeIndex === lastIndex && styles.arrowDisabled]}
              testID="lessons-playlist-next"
            >
              <Feather name="chevron-right" size={36} color={COLORS.white} />
            </TouchableOpacity>
          </>
        )}

        <View style={[styles.bottomBar, bottomInset > 0 && { paddingBottom: bottomInset + 8 }]} pointerEvents="box-none">
          <Text style={styles.titleText} numberOfLines={2}>
            {activeVideo?.name ?? ''}
          </Text>
          <View style={styles.controlsRow} pointerEvents="box-none">
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={isPaused ? 'Play' : 'Pause'}
              onPress={togglePause}
              style={styles.playPauseButton}
              hitSlop={hitSlop}
              testID="lessons-playlist-playpause"
            >
              <Feather name={isPaused ? 'play' : 'pause'} size={22} color={COLORS.white} />
            </TouchableOpacity>
            <View style={styles.progressTrack} pointerEvents="none">
              <View style={[styles.progressFill, { width: `${progressRatio * 100}%` }]} />
            </View>
          </View>
        </View>

        {endCue && (
          <Animated.View style={[styles.endCue, { opacity: endCueOpacity }]} pointerEvents="none">
            <Text style={styles.endCueText}>{endCue === 'last' ? 'This is the last video' : 'This is the first video'}</Text>
          </Animated.View>
        )}
      </View>
    </View>
  );
};

interface PlaylistPageProps {
  video: PoolVideo;
  width: number;
  height: number;
  isActive: boolean;
  isMounted: boolean;
  isPaused: boolean;
  onProgressUpdate?: (positionMillis: number, durationMillis: number) => void;
  onTogglePause: () => void;
}

const TAP_SLOP = 12;

const PlaylistPage = memo(({ video, width, height, isActive, isMounted, isPaused, onProgressUpdate, onTogglePause }: PlaylistPageProps) => {
  const thumbnail = mbGetMediumImage(video.thumbnail);

  // Tap-to-pause is detected from raw touches so that a swipe (which the pager
  // may or may not claim, e.g. single-video collections have no pager) never
  // counts as a tap.
  const touchStart = useRef<{ x: number; y: number } | undefined>(undefined);
  const onTouchStart = useCallback((event: GestureResponderEvent) => {
    touchStart.current = { x: event.nativeEvent.pageX, y: event.nativeEvent.pageY };
  }, []);
  const onTouchEnd = useCallback(
    (event: GestureResponderEvent) => {
      const start = touchStart.current;
      touchStart.current = undefined;
      if (!start || !isActive) {
        return;
      }
      if (Math.abs(event.nativeEvent.pageX - start.x) <= TAP_SLOP && Math.abs(event.nativeEvent.pageY - start.y) <= TAP_SLOP) {
        onTogglePause();
      }
    },
    [isActive, onTogglePause]
  );

  return (
    <View style={[styles.page, { width, height }]}>
      {isMounted ? (
        <View style={styles.pageInner} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} accessible accessibilityRole="button" accessibilityLabel={isPaused ? 'Play' : 'Pause'}>
          <MB_VideoPlayer
            sourceUrl={video.url}
            containerStyle={styles.player}
            // Neighbour pages are mounted paused so the stream is prepared before the swipe.
            shouldPlay={isActive && !isPaused}
            isLooping
            isMuted={false}
            showControls={false}
            resizeMode={MB_VIDEO_PLAYER_RESIZE_MODE.contain}
            onProgressUpdate={onProgressUpdate}
            loadingComponent={isActive ? <ActivityIndicator color={COLORS.white} size="large" /> : undefined}
          />
          {isActive && isPaused && (
            <View style={styles.pausedBadge} pointerEvents="none">
              <Feather name="play" size={40} color={COLORS.white} />
            </View>
          )}
        </View>
      ) : (
        <View style={styles.pageInner}>{thumbnail ? <MB_Image source={thumbnail} style={styles.placeholderThumbnail} resizeMode="contain" disableInteraction /> : null}</View>
      )}
    </View>
  );
});

const hitSlop = { top: 12, bottom: 12, left: 12, right: 12 };

export { LessonsPlaylistViewer };
export type { LessonsPlaylistViewerProps };

const styles = StyleSheet.create({
  modalChildrenWrapper: {
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.black,
  },
  container: {
    flex: 1,
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.black,
  },
  list: {
    flex: 1,
  },
  page: {
    backgroundColor: COLORS.black,
  },
  pageInner: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  player: {
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.black,
  },
  placeholderThumbnail: {
    width: '100%',
    height: '60%',
  },
  pausedBadge: {
    position: 'absolute',
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    paddingStart: 6,
    backgroundColor: mbApplyTransparency(COLORS.black, 0.45),
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'space-between',
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: isMobileApp ? 8 : 16,
  },
  counterText: mbTextStyles([
    textStyles.smallText,
    {
      color: COLORS.white,
      fontWeight: 'bold',
      textShadowColor: mbApplyTransparency(COLORS.black, 0.8),
      textShadowRadius: 4,
    },
  ]),
  closeButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: mbApplyTransparency(COLORS.black, 0.45),
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -28,
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: mbApplyTransparency(COLORS.black, 0.45),
  },
  arrowLeft: {
    left: 24,
  },
  arrowRight: {
    right: 24,
  },
  arrowDisabled: {
    opacity: 0.35,
  },
  bottomBar: {
    paddingHorizontal: 16,
    paddingBottom: isMobileApp ? 16 : 24,
  },
  titleText: mbTextStyles([
    textStyles.smallText,
    {
      color: COLORS.white,
      textAlign: 'left',
      marginBottom: 10,
      textShadowColor: mbApplyTransparency(COLORS.black, 0.8),
      textShadowRadius: 4,
    },
  ]),
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  playPauseButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginEnd: 12,
    backgroundColor: mbApplyTransparency(COLORS.black, 0.45),
  },
  progressTrack: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: mbApplyTransparency(COLORS.white, 0.3),
  },
  progressFill: {
    height: '100%',
    backgroundColor: COLORS.white,
  },
  endCue: {
    position: 'absolute',
    alignSelf: 'center',
    bottom: 110,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: mbApplyTransparency(COLORS.black, 0.7),
  },
  endCueText: mbTextStyles([
    textStyles.smallText,
    {
      color: COLORS.white,
    },
  ]),
});
